import NiceModal from "@ebay/nice-modal-react";
import { Divider, LinkProps, Stack, StackProps, SxProps } from "@mui/material";
import React, { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Accordions } from "./Accordions";
import * as Styled from "./AccordionsMobileSlideUp.styled";
import { Modals } from "./Modals";

export type AccordionsMobileSlideUpProps = {
    accordions: {
        title: React.ReactNode | string;
        rows: {
            title: React.ReactNode | string;
            popUpContentTitle?: React.ReactNode | string;
            popUpContent?: React.ReactNode;
        }[];
    }[];
    confirmButtonText?: string;
    sx?: SxProps;
    ClickableRowWrapper?: React.ComponentType<LinkProps>;
    RowsContainer?: React.ComponentType<StackProps>;
}

const defaultComponents: {
    RowsContainer: Required<AccordionsMobileSlideUpProps>["RowsContainer"];
    ClickableRowWrapper: Required<AccordionsMobileSlideUpProps>["ClickableRowWrapper"];
} = {
    RowsContainer: (props) => <Stack divider={<Divider />} {...props} />,
    ClickableRowWrapper: (props) => <Styled.AccordionsMobileSlideUpRowWrapperLink {...props} />,
};

export const AccordionsMobileSlideUp: FC<AccordionsMobileSlideUpProps> = ({
    accordions,
    confirmButtonText,
    sx,
    ClickableRowWrapper = defaultComponents.ClickableRowWrapper,
    RowsContainer = defaultComponents.RowsContainer,
}) => {
    const { t } = useTranslation();

    const handleShowBodyContent = useCallback((content: React.ReactNode, contentTitle: React.ReactNode) => {
        NiceModal.show(Modals.ModalSlideUp, {
            title: contentTitle,
            content: <>{content}</>,
            confirmButtonText: confirmButtonText ?? t("global.iUnderstand")
        });
    }, [t]);

    const renderRow = useCallback(
        (
            row: AccordionsMobileSlideUpProps["accordions"][number]["rows"][number],
            rowKey: string
        ) => {
            if (row.popUpContent) {
                return (
                    <ClickableRowWrapper
                        key={rowKey}
                        onClick={(ev) => {
                            ev.preventDefault();
                            handleShowBodyContent(row.popUpContent, row.popUpContentTitle);
                        }}>
                        <Styled.AccordionsSlideUpTitleBox>
                            {row.title}
                        </Styled.AccordionsSlideUpTitleBox>
                        <Styled.SelectArrowOrangeRight />
                    </ClickableRowWrapper>
                );
            }
            return <span key={rowKey}>{row.title}</span>;
        },
        [ClickableRowWrapper, handleShowBodyContent]
    );

    const rows = useMemo<{
        id: string,
        TitleContent: React.JSX.Element,
        Bodycontent: React.JSX.Element
    }[]>(() => {
        return accordions.map((accordion, index) => ({
            id: `accordion-slideup-${index}`,
            TitleContent: <>{accordion.title}</>,
            Bodycontent: (
                <RowsContainer>
                    {accordion.rows?.map((row, rowIndex) =>
                        renderRow(row, `accordion-slideup-${index}-row-${rowIndex}`)
                    )}
                </RowsContainer>
            )
        }));
    }, [accordions, ClickableRowWrapper, RowsContainer, renderRow]);

    return (
        <Accordions sx={sx} rows={rows ?? []} />
    );
}
